// Runtime validation for everything that arrives over the network. Hand-written
// to keep the agent dependency-free.

import {
  EFFECTS,
  LIMITS,
  RATES,
  type AgentConfig,
  type AgentReport,
  type ClientMessage,
  type DeviceInfo,
  type HouseView,
  type Rgb,
  type ServerMessage,
  type Show,
  type ShowSpec,
  type StrandConfig,
} from "./protocol.ts";
import { parseProgram } from "./program.ts";

export class ProtocolError extends Error {}

const ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

type Obj = Record<string, unknown>;

function fail(message: string): never {
  throw new ProtocolError(message);
}

function obj(value: unknown, field: string): Obj {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    fail(`${field} must be an object`);
  }
  return value as Obj;
}

function str(value: unknown, field: string, maxLength = 256): string {
  if (typeof value !== "string" || value.length > maxLength) {
    fail(`${field} must be a string of at most ${maxLength} characters`);
  }
  return value;
}

export function id(value: unknown, field: string): string {
  if (typeof value !== "string" || !ID_PATTERN.test(value)) {
    fail(`${field} must be 1-64 letters, digits, '-' or '_'`);
  }
  return value;
}

function num(value: unknown, field: string, min: number, max: number): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value < min || value > max) {
    fail(`${field} must be a number from ${min} to ${max}`);
  }
  return value;
}

function int(value: unknown, field: string, min: number, max: number): number {
  const n = num(value, field, min, max);
  if (!Number.isInteger(n)) fail(`${field} must be a whole number`);
  return n;
}

function epochMs(value: unknown, field: string): number {
  return num(value, field, 0, 8.64e15);
}

export function rgb(value: unknown, field: string): Rgb {
  if (!Array.isArray(value) || value.length !== 3) fail(`${field} must be [r, g, b]`);
  return [
    int(value[0], `${field}[0]`, 0, 255),
    int(value[1], `${field}[1]`, 0, 255),
    int(value[2], `${field}[2]`, 0, 255),
  ];
}

function bool(value: unknown, field: string): boolean {
  if (typeof value !== "boolean") fail(`${field} must be true or false`);
  return value;
}

function oneOf<T extends string | number>(value: unknown, field: string, allowed: readonly T[]): T {
  if (!allowed.includes(value as T)) fail(`${field} must be one of ${allowed.join(", ")}`);
  return value as T;
}

function palette(value: unknown, field: string): Rgb[] {
  if (!Array.isArray(value) || value.length < 1 || value.length > LIMITS.maxPaletteColors) {
    fail(`${field} must have 1 to ${LIMITS.maxPaletteColors} colors`);
  }
  return value.map((c, i) => rgb(c, `${field}[${i}]`));
}

export function showSpec(value: unknown): ShowSpec {
  const v = obj(value, "show");
  switch (v.kind) {
    case "off":
      return { kind: "off" };
    case "solid":
      return {
        kind: "solid",
        rgb: rgb(v.rgb, "show.rgb"),
        brightness: int(v.brightness, "show.brightness", 1, 100),
      };
    case "beat":
    case "chase":
      return {
        kind: v.kind,
        bpm: num(v.bpm, "show.bpm", LIMITS.minBpm, LIMITS.maxBpm),
        palette: palette(v.palette, "show.palette"),
        brightness: int(v.brightness, "show.brightness", 1, 100),
        ...(v.startEpochMs === undefined
          ? {}
          : { startEpochMs: epochMs(v.startEpochMs, "show.startEpochMs") }),
      };
    case "effect": {
      const effect = oneOf(v.effect, "show.effect", EFFECTS);
      const rate = oneOf(v.rate, "show.rate", RATES);
      const bpm = num(v.bpm, "show.bpm", LIMITS.minBpm, LIMITS.maxBpm);
      return {
        kind: "effect",
        effect,
        rate,
        bpm,
        palette: palette(v.palette, "show.palette"),
        brightness: int(v.brightness, "show.brightness", 1, 100),
        ...(v.startEpochMs === undefined
          ? {}
          : { startEpochMs: epochMs(v.startEpochMs, "show.startEpochMs") }),
      };
    }
    default:
      fail("show.kind must be off, solid, beat, chase or effect");
  }
}

export function show(value: unknown): Show {
  const v = obj(value, "show");
  return { ...showSpec(v), t: epochMs(v.t, "show.t") };
}

function device(value: unknown, field: string): DeviceInfo {
  const v = obj(value, field);
  const d: DeviceInfo = {
    id: str(v.id, `${field}.id`, 64),
    sku: str(v.sku, `${field}.sku`, 32),
    lastSeen: epochMs(v.lastSeen, `${field}.lastSeen`),
  };
  if (v.ip !== undefined) d.ip = str(v.ip, `${field}.ip`, 64);
  return d;
}

function report(value: unknown): AgentReport {
  const v = obj(value, "report");
  if (!Array.isArray(v.devices) || v.devices.length > LIMITS.maxDevices) {
    fail(`report.devices must be a list of at most ${LIMITS.maxDevices}`);
  }
  return {
    driver: str(v.driver, "report.driver", 32),
    devices: v.devices.map((d, i) => device(d, `report.devices[${i}]`)),
    clockSkewMs: v.clockSkewMs === null ? null : num(v.clockSkewMs, "report.clockSkewMs", -8.64e7, 8.64e7),
  };
}

function agentConfig(m: Obj): AgentConfig {
  const config: AgentConfig = {
    offsetMs: int(m.offsetMs, "offsetMs", -LIMITS.maxOffsetMs, LIMITS.maxOffsetMs),
  };
  if (m.position !== undefined) config.position = int(m.position, "position", 0, LIMITS.maxPosition);
  if (m.style !== undefined) {
    const s = obj(m.style, "style");
    config.style = { realtime: s.realtime === true, flash: s.flash === true };
  }
  if (m.allowedDevices !== undefined) {
    if (!Array.isArray(m.allowedDevices) || m.allowedDevices.length > LIMITS.maxDevices) {
      fail("allowedDevices must be a list");
    }
    config.allowedDevices = m.allowedDevices.map((d, i) => str(d, `allowedDevices[${i}]`, 64));
  }
  if (m.strands !== undefined) {
    if (!Array.isArray(m.strands) || m.strands.length > LIMITS.maxDevices) {
      fail(`strands must be a list of at most ${LIMITS.maxDevices}`);
    }
    config.strands = m.strands.map((s, i) => strand(s, `strands[${i}]`));
    const ids = new Set(config.strands.map((s) => s.id));
    if (ids.size !== config.strands.length) fail("strands must not list a device twice");
  }
  return config;
}

function strand(value: unknown, field: string): StrandConfig {
  const v = obj(value, field);
  const s: StrandConfig = {
    id: str(v.id, `${field}.id`, 64),
    order: int(v.order, `${field}.order`, 0, LIMITS.maxDevices - 1),
    realtime: bool(v.realtime, `${field}.realtime`),
  };
  if (v.segments !== undefined) s.segments = int(v.segments, `${field}.segments`, 1, LIMITS.maxSections);
  if (v.reverse !== undefined) s.reverse = bool(v.reverse, `${field}.reverse`);
  return s;
}

function parseJson(text: string): Obj {
  if (text.length > LIMITS.maxMessageBytes) fail("message too large");
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    fail("message is not valid JSON");
  }
  return obj(value, "message");
}

export function parseClientMessage(text: string): ClientMessage {
  const m = parseJson(text);
  switch (m.type) {
    case "hello": {
      if (m.role === "agent" && m.token !== undefined) {
        return {
          type: "hello",
          role: "agent",
          token: str(m.token, "token", 256),
          agentVersion: str(m.agentVersion, "agentVersion", 32),
        };
      }
      const room = id(m.room, "room");
      if (m.role === "controller") {
        return m.key === undefined
          ? { type: "hello", role: "controller", room }
          : { type: "hello", role: "controller", room, key: str(m.key, "key", 256) };
      }
      const key = str(m.key, "key", 256);
      if (m.role !== "agent") fail("role must be agent or controller");
      return {
        type: "hello",
        role: "agent",
        room,
        key,
        house: id(m.house, "house"),
        position: int(m.position, "position", 0, LIMITS.maxPosition),
        agentVersion: str(m.agentVersion, "agentVersion", 32),
      };
    }
    case "status":
      return { type: "status", report: report(m.report) };
    case "ping":
      return { type: "ping", clientTime: epochMs(m.clientTime, "clientTime") };
    case "set_show":
      return { type: "set_show", show: showSpec(m.show) };
    case "now_playing": {
      if (m.song === null) return { type: "now_playing", song: null };
      const song = obj(m.song, "song");
      const songId = str(song.songId, "song.songId", 16);
      if (!/^[0-9a-f]{16}$/.test(songId)) fail("song.songId must be a library song id");
      return {
        type: "now_playing",
        song: {
          songId,
          title: str(song.title, "song.title", 200),
          startEpochMs: epochMs(song.startEpochMs, "song.startEpochMs"),
          duration: num(song.duration, "song.duration", 0, 4 * 3600),
        },
      };
    }
    case "set_offset":
      return {
        type: "set_offset",
        house: id(m.house, "house"),
        offsetMs: int(m.offsetMs, "offsetMs", -LIMITS.maxOffsetMs, LIMITS.maxOffsetMs),
      };
    default:
      fail(`unknown message type: ${String(m.type)}`);
  }
}

// Messages from the hub. Agents still validate them so a hub bug cannot crash
// a house or push an unsafe show. Controller-only messages (houses) are passed
// through after a shallow check because only the dashboard reads them.
export function parseServerMessage(text: string): ServerMessage {
  const m = parseJson(text);
  switch (m.type) {
    case "welcome": {
      const msg: ServerMessage = {
        type: "welcome",
        role: m.role === "agent" ? "agent" : "controller",
        serverTime: epochMs(m.serverTime, "serverTime"),
        show: m.show === null ? null : show(m.show),
      };
      if (m.offsetMs !== undefined) {
        msg.offsetMs = int(m.offsetMs, "offsetMs", -LIMITS.maxOffsetMs, LIMITS.maxOffsetMs);
      }
      return msg;
    }
    case "show":
      return { type: "show", show: show(m.show) };
    case "config":
      return { type: "config", ...agentConfig(m) };
    case "song": {
      if (m.cue === null) return { type: "song", cue: null };
      const cue = obj(m.cue, "cue");
      const songId = str(cue.songId, "cue.songId", 16);
      if (!/^[0-9a-f]{16}$/.test(songId)) fail("cue.songId must be a library song id");
      let program;
      try {
        program = parseProgram(cue.program);
      } catch (e) {
        fail(`cue.program: ${(e as Error).message}`);
      }
      return {
        type: "song",
        cue: {
          songId,
          startEpochMs: epochMs(cue.startEpochMs, "cue.startEpochMs"),
          duration: num(cue.duration, "cue.duration", 0, 4 * 3600),
          program: program!,
          ...(cue.preview === true ? { preview: true } : {}),
        },
      };
    }
    case "pong":
      return {
        type: "pong",
        clientTime: epochMs(m.clientTime, "clientTime"),
        serverTime: epochMs(m.serverTime, "serverTime"),
      };
    case "houses":
      if (!Array.isArray(m.houses)) fail("houses must be a list");
      return { type: "houses", houses: m.houses as HouseView[] };
    case "error":
      return { type: "error", message: str(m.message, "message", 1000) };
    default:
      fail(`unknown message type: ${String(m.type)}`);
  }
}
